import React, { useState, useEffect } from 'react';
import { 
  XCircle, 
  Calendar,
  TrendingUp,
  Activity,
  Heart,
  Activity as ActivityIcon,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { api } from '../services/main-api/apiClient';
import toast from 'react-hot-toast';

const PatientHealthTrends = ({ patient, onClose }) => {
  const [selectedRange, setSelectedRange] = useState('30days');
  const [bloodPressureData, setBloodPressureData] = useState([]);
  const [heartRateData, setHeartRateData] = useState([]);
  const [filteredBloodPressureData, setFilteredBloodPressureData] = useState([]);
  const [filteredHeartRateData, setFilteredHeartRateData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [error, setError] = useState(null);

  // Fetch real patient vital signs data
  const fetchPatientVitals = async (retryCount = 0) => {
    if (!patient?.id) {
      console.error('❌ No patient ID provided');
      setError('Patient ID is required');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      console.log('🔍 PatientHealthTrends: Fetching vital signs for patient:', patient.id);
      console.log('🔍 PatientHealthTrends: Patient object:', patient);
      console.log('🔍 PatientHealthTrends: API endpoint being called: /patients/${patient.id}/vitals');
      console.log('🔍 PatientHealthTrends: Retry attempt:', retryCount);
      
      // Check if token exists
      const token = localStorage.getItem('token');
      console.log('🔍 PatientHealthTrends: Auth token exists:', !!token);
      console.log('🔍 PatientHealthTrends: Auth token preview:', token ? `${token.substring(0, 20)}...` : 'No token');

      const response = await api.get(`/patients/${patient.id}/vitals`);
      console.log('📊 PatientHealthTrends: API response:', response);
      console.log('📊 PatientHealthTrends: response.data:', response.data);
      console.log('📊 PatientHealthTrends: response.status:', response.status);
      console.log('📊 PatientHealthTrends: response.headers:', response.headers);
      console.log('📊 PatientHealthTrends: response.config:', response.config);

      if (!response.data || !Array.isArray(response.data) || response.data.length === 0) {
        console.warn('⚠️ PatientHealthTrends: No vital signs data received from backend');
        console.log('🔍 PatientHealthTrends: Response structure:', {
          hasData: !!response.data,
          isArray: Array.isArray(response.data),
          length: response.data?.length,
          dataType: typeof response.data,
          data: response.data
        });
        setError('No vital signs data available for this patient');
        setLoading(false);
        return;
      }

      // Transform the API data to match our chart format
      const transformedData = response.data.map(vital => {
        const date = new Date(vital.created_at).toISOString().split('T')[0];
        
        return {
          date: date,
          // Blood pressure data
          sbp: vital.blood_pressure_systolic || vital.bp_sys || null,
          dbp: vital.blood_pressure_diastolic || vital.bp_dia || null,
          map: vital.blood_pressure_systolic && vital.blood_pressure_diastolic ? 
            Math.round((vital.blood_pressure_systolic + 2 * vital.blood_pressure_diastolic) / 3) : null,
          
          // Heart rate data
          hr: vital.heart_rate || vital.hr || null,
          hrv: vital.heart_rate_variability || vital.hrv || null,
          rr: vital.rhythm === 'Regular' ? 1 : 0, // Rhythm regularity (1 = regular, 0 = irregular)
          
          // Additional metadata
          quality_flag: vital.quality_flag,
          notes: vital.notes,
          session_id: vital.session_id,
          source_device: vital.source_device
        };
      }).filter(item => item.hr !== null || item.sbp !== null); // Filter out entries with no data

      console.log('🔄 PatientHealthTrends: Transformed vital signs data:', transformedData);

      // Group data by date and calculate averages with individual readings for all vital signs
      const groupDataByDate = (data) => {
        const grouped = {};
        data.forEach(item => {
          if (!grouped[item.date]) {
            grouped[item.date] = {
              date: item.date,
              sbp_readings: [],
              dbp_readings: [],
              map_readings: [],
              hr_readings: [],
              hrv_readings: [],
              rhythm_readings: [],
              sbp_average: 0,
              dbp_average: 0,
              map_average: 0,
              hr_average: 0,
              hrv_average: 0,
              metadata: item
            };
          }
          
          // Collect readings for each vital sign
          if (item.sbp !== null) grouped[item.date].sbp_readings.push(item.sbp);
          if (item.dbp !== null) grouped[item.date].dbp_readings.push(item.dbp);
          if (item.map !== null) grouped[item.date].map_readings.push(item.map);
          if (item.hr !== null) grouped[item.date].hr_readings.push(item.hr);
          if (item.hrv !== null) grouped[item.date].hrv_readings.push(item.hrv);
          if (item.rr !== null && item.rr !== undefined) grouped[item.date].rhythm_readings.push(item.rr);
        });
        
        // Calculate averages for each vital sign
        Object.values(grouped).forEach(group => {
          if (group.sbp_readings.length > 0) {
            group.sbp_average = group.sbp_readings.reduce((sum, val) => sum + val, 0) / group.sbp_readings.length;
          }
          if (group.dbp_readings.length > 0) {
            group.dbp_average = group.dbp_readings.reduce((sum, val) => sum + val, 0) / group.dbp_readings.length;
          }
          if (group.map_readings.length > 0) {
            group.map_average = group.map_readings.reduce((sum, val) => sum + val, 0) / group.map_readings.length;
          }
          if (group.hr_readings.length > 0) {
            group.hr_average = group.hr_readings.reduce((sum, val) => sum + val, 0) / group.hr_readings.length;
          }
          if (group.hrv_readings.length > 0) {
            group.hrv_average = group.hrv_readings.reduce((sum, val) => sum + val, 0) / group.hrv_readings.length;
          }
        });
        
        return Object.values(grouped);
      };

      // Group all data by date
      const groupedData = groupDataByDate(transformedData);

      // Separate blood pressure and heart rate data
      const bpData = groupedData.filter(item => item.sbp_readings.length > 0 || item.dbp_readings.length > 0 || item.map_readings.length > 0);
      const hrData = groupedData.filter(item => item.hr_readings.length > 0 || item.hrv_readings.length > 0);

      setBloodPressureData(bpData);
      setHeartRateData(hrData);
      setFilteredBloodPressureData(bpData);
      setFilteredHeartRateData(hrData);

      console.log('✅ PatientHealthTrends: Vital signs data loaded successfully');
      console.log('   📈 Blood pressure records:', bpData.length);
      console.log('   ❤️ Heart rate records:', hrData.length);
      
      // Debug blood pressure data structure
      if (bpData.length > 0) {
        console.log('🔍 Sample BP data structure:', bpData[0]);
        console.log('🔍 SBP readings:', bpData[0].sbp_readings);
        console.log('🔍 DBP readings:', bpData[0].dbp_readings);
        console.log('🔍 MAP readings:', bpData[0].map_readings);
      }

    } catch (error) {
      console.error('❌ PatientHealthTrends: Failed to fetch patient vital signs:', error);
      console.error('❌ PatientHealthTrends: Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText,
        config: error.config
      });
      
      let errorMessage = 'Failed to load patient vital signs data';
      
      if (error.response?.status === 401) {
        errorMessage = 'Authentication failed. Please log in again.';
        toast.error('Session expired. Please log in again.');
      } else if (error.response?.status === 403) {
        errorMessage = 'Access denied. You do not have permission to view this patient\'s data.';
        toast.error('Access denied');
      } else if (error.response?.status === 404) {
        errorMessage = 'Patient not found or no vital signs data available.';
        toast.error('No data available');
      } else if (error.response?.status >= 500) {
        errorMessage = 'Server error. Please try again later.';
        toast.error('Server error');
      } else if (error.message) {
        errorMessage = `Failed to load patient vital signs data: ${error.message}`;
        toast.error('Failed to load health trends data');
      }
      
      // Retry mechanism for network errors
      if (retryCount < 2 && (error.code === 'NETWORK_ERROR' || error.message.includes('Network Error'))) {
        console.log(`🔄 PatientHealthTrends: Retrying in 1 second... (attempt ${retryCount + 1})`);
        setTimeout(() => {
          fetchPatientVitals(retryCount + 1);
        }, 1000);
        return;
      }
      
      setError(errorMessage);
      
      // Set empty data on error
      setBloodPressureData([]);
      setHeartRateData([]);
      setFilteredBloodPressureData([]);
      setFilteredHeartRateData([]);
    } finally {
      setLoading(false);
    }
  };

  // Test backend connectivity
  const testBackendConnection = async () => {
    try {
      console.log('🔍 Testing backend connection...');
      const response = await fetch('http://localhost:8001/api/health');
      console.log('✅ Backend health check response:', response);
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Backend health data:', data);
      }
    } catch (error) {
      console.error('❌ Backend connection test failed:', error);
    }
  };

  useEffect(() => {
    testBackendConnection();
    fetchPatientVitals();
  }, [patient?.id]);

  // Apply default date range when data is loaded
  useEffect(() => {
    if (bloodPressureData.length > 0 || heartRateData.length > 0) {
      console.log('🔍 Applying default date range (30days)');
      handleDateRangeChange('30days');
    }
  }, [bloodPressureData, heartRateData]);



  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const month = (date.getMonth() + 1).toString().padStart(2, '0'); // MM format
    const day = date.getDate().toString().padStart(2, '0'); // DD format
    return `${month}/${day}`; // MM/DD format
  };

  const formatValue = (value, maxValue) => {
    return value > maxValue ? `>${maxValue}` : value.toString();
  };

  // Calendar utility functions
  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const formatDateForInput = (date) => {
    return date.toISOString().split('T')[0];
  };

  const isDateInRange = (date, startDate, endDate) => {
    if (!startDate || !endDate) return false;
    const checkDate = new Date(date);
    const start = new Date(startDate);
    const end = new Date(endDate);
    return checkDate >= start && checkDate <= end;
  };

  const handleCustomRangeApply = () => {
    if (customStartDate && customEndDate) {
      setSelectedRange('custom');
      setShowCalendar(false);
      filterDataByDateRange(customStartDate, customEndDate);
    }
  };

  const filterDataByDateRange = (startDate, endDate) => {
    // Normalize dates to compare only the date part (YYYY-MM-DD)
    const start = new Date(startDate);
    const end = new Date(endDate);
    const startDateStr = start.toISOString().split('T')[0];
    const endDateStr = end.toISOString().split('T')[0];
    
    console.log('🔍 Filtering data by date range:', {
      startDate: startDateStr,
      endDate: endDateStr,
      totalBPData: bloodPressureData.length,
      totalHRData: heartRateData.length
    });
    
    // Filter blood pressure data
    const filteredBP = bloodPressureData.filter(item => {
      return item.date >= startDateStr && item.date <= endDateStr;
    });
    
    // Filter heart rate data
    const filteredHR = heartRateData.filter(item => {
      return item.date >= startDateStr && item.date <= endDateStr;
    });
    
    console.log('🔍 Filtered results:', {
      filteredBPCount: filteredBP.length,
      filteredHRCount: filteredHR.length,
      bpDates: filteredBP.map(item => item.date),
      hrDates: filteredHR.map(item => item.date)
    });
    
    // Debug: Show available dates in the data
    if (bloodPressureData.length > 0) {
      console.log('🔍 Available BP dates:', bloodPressureData.map(item => item.date));
    }
    if (heartRateData.length > 0) {
      console.log('🔍 Available HR dates:', heartRateData.map(item => item.date));
    }
    
    // If no data found for the selected range, show the most recent data
    if (filteredBP.length === 0 && filteredHR.length === 0) {
      console.log('⚠️ No data found for selected range, showing most recent data');
      
      // Get the most recent date from available data
      const allDates = [...new Set([
        ...bloodPressureData.map(item => item.date),
        ...heartRateData.map(item => item.date)
      ])].sort();
      
      if (allDates.length > 0) {
        const mostRecentDate = allDates[allDates.length - 1];
        console.log('🔍 Showing most recent date:', mostRecentDate);
        
        const recentBP = bloodPressureData.filter(item => item.date === mostRecentDate);
        const recentHR = heartRateData.filter(item => item.date === mostRecentDate);
        
        setFilteredBloodPressureData(recentBP);
        setFilteredHeartRateData(recentHR);
        return;
      }
    }
    
    setFilteredBloodPressureData(filteredBP);
    setFilteredHeartRateData(filteredHR);
  };

  const handleDateRangeChange = (range) => {
    setSelectedRange(range);
    
    const today = new Date();
    let startDate, endDate;
    
    switch (range) {
      case 'today':
        startDate = today;
        endDate = today;
        break;
      case '7days':
        startDate = new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000);
        endDate = today;
        break;
      case '30days':
        startDate = new Date(today.getTime() - 29 * 24 * 60 * 60 * 1000);
        endDate = today;
        break;
      case 'custom':
        if (customStartDate && customEndDate) {
          startDate = new Date(customStartDate);
          endDate = new Date(customEndDate);
        } else {
          return;
        }
        break;
      default:
        return;
    }
    
    filterDataByDateRange(startDate, endDate);
  };

  const renderChart = (data, title, yAxisRange, series, tooltipFormat) => {
    console.log(`🔍 Rendering chart: ${title}`, {
      dataLength: data.length,
      data: data,
      series: series,
      yAxisRange: yAxisRange
    });
    
    // Use the component's hover state with chart identifier
    
    const maxY = yAxisRange.max;
    const minY = yAxisRange.min;
    const chartHeight = 350; // Reduced chart height for tighter spacing
    const chartWidth = Math.min(800, window.innerWidth - 100);
    const padding = 60; // Reduced padding for tighter spacing
    const bottomPadding = 30; // Further reduced bottom padding for tighter spacing

    // Generate all dates in the selected range
    const generateDateRange = () => {
      const today = new Date();
      let startDate, endDate;
      
      switch (selectedRange) {
        case 'today':
          startDate = new Date(today);
          endDate = new Date(today);
          break;
        case '7days':
          startDate = new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000);
          endDate = new Date(today);
          break;
        case '30days':
          startDate = new Date(today.getTime() - 29 * 24 * 60 * 60 * 1000);
          endDate = new Date(today);
          break;
        case 'custom':
          if (customStartDate && customEndDate) {
            startDate = new Date(customStartDate);
            endDate = new Date(customEndDate);
          } else {
            // Fallback to 30 days if custom range not set
            startDate = new Date(today.getTime() - 29 * 24 * 60 * 60 * 1000);
            endDate = new Date(today);
          }
          break;
        default:
          // Default to 30 days
          startDate = new Date(today.getTime() - 29 * 24 * 60 * 60 * 1000);
          endDate = new Date(today);
      }
      
      const dates = [];
      for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
        dates.push(new Date(d));
      }
      
      console.log('🔍 Generated date range:', {
        selectedRange: selectedRange,
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0],
        totalDates: dates.length,
        dates: dates.map(d => d.toISOString().split('T')[0])
      });
      
      return dates;
    };

    const allDates = generateDateRange();

    const getY = (value) => {
      const clampedValue = Math.min(value, maxY);
      return padding + (chartHeight - padding - bottomPadding) * (1 - (clampedValue - minY) / (maxY - minY));
    };

    const getX = (index) => {
      // Handle case when there's only one date (like "Today")
      if (allDates.length === 1) {
        const centerX = padding + (chartWidth - padding * 2) / 2;
        console.log(`🔍 Single date positioning - index: ${index}, centerX: ${centerX}`);
        return centerX;
      }
      const x = padding + (index * (chartWidth - padding * 2)) / (allDates.length - 1);
      console.log(`🔍 Multi-date positioning - index: ${index}, totalDates: ${allDates.length}, x: ${x}`);
      return x;
    };

    const chartId = title.replace(/\s+/g, '-').toLowerCase(); // Create unique chart ID
    
    const handleMouseEnter = (event, item, seriesKey, seriesIndex) => {
      event.stopPropagation(); // Prevent event bubbling
      
      const rect = event.currentTarget.getBoundingClientRect();
      const svgRect = event.currentTarget.closest('svg').getBoundingClientRect();
      
      setHoveredPoint({
        x: event.clientX - svgRect.left,
        y: event.clientY - svgRect.top,
        data: item,
        series: seriesKey,
        seriesIndex: seriesIndex,
        chartId: chartId,
        tooltipFormat
      });
    };

    const handleMouseLeave = (event) => {
      event.stopPropagation(); // Prevent event bubbling
      // Only clear if this chart's tooltip is currently shown
      setHoveredPoint(prev => {
        if (prev && prev.chartId === chartId) {
          return null;
        }
        return prev;
      });
    };

    return (
      <div className="bg-white p-6 rounded-lg border border-[#E5E7EB] mb-6 shadow-sm">
        <h3 className="text-lg font-semibold text-[#0B1C2C] text-center mb-4">{title}</h3>
        
        <div className="relative overflow-x-auto" style={{ height: 410, position: 'relative' }}>
          <svg width={chartWidth} height={410} className="mx-auto">
            {/* Grid lines */}
            {Array.from({ length: 11 }, (_, i) => {
              const y = padding + (i * (chartHeight - padding - bottomPadding)) / 10;
              return (
                <line
                  key={i}
                  x1={padding}
                  y1={y}
                  x2={chartWidth - padding}
                  y2={y}
                  stroke="#F3F4F6"
                  strokeWidth="1"
                />
              );
            })}
            
            {/* Vertical grid lines */}
            {allDates.map((date, index) => {
              const x = getX(index);
              
              // Show all grid lines but with lighter stroke for longer ranges
              const totalDates = allDates.length;
              let strokeWidth = "1";
              let strokeColor = "#F3F4F6";
              
              if (totalDates > 14) {
                strokeWidth = "0.5";
                strokeColor = "#F9FAFB";
              }
              
              return (
                <line
                  key={`v-${index}`}
                  x1={x}
                  y1={padding}
                  x2={x}
                  y2={chartHeight + padding - bottomPadding}
                  stroke={strokeColor}
                  strokeWidth={strokeWidth}
                />
              );
            })}
            
            {/* Y-axis labels with reduced spacing */}
            {[200, 180, 160, 140, 120, 100, 80, 60, 40, 20, 0].map((value, i) => {
              const y = padding + (i * (chartHeight - padding - bottomPadding)) / 10;
              return (
                <text
                  key={i}
                  x={padding - 20}
                  y={y + 3}
                  textAnchor="end"
                  fontSize="12"
                  fontWeight="500"
                  fill="#374151"
                >
                  {value}
                </text>
              );
            })}

            {/* X-axis labels */}
            {allDates.map((date, index) => {
              const x = getX(index);
              
              // Show all labels but with smaller font and tighter spacing for longer ranges
              const totalDates = allDates.length;
              let fontSize = "11";
              let yOffset = 10; // Reduced for tighter spacing
              
              if (totalDates === 1) {
                // Special case for single date (Today) - even tighter spacing
                yOffset = 8;
              } else if (totalDates > 14) {
                fontSize = "9";
                yOffset = 15;
              } else if (totalDates > 21) {
                fontSize = "8";
                yOffset = 18;
              }
              
              return (
                <text
                  key={index}
                  x={x}
                  y={chartHeight + padding - bottomPadding + yOffset}
                  textAnchor="middle"
                  fontSize={fontSize}
                  fontWeight="500"
                  fill="#374151"
                  style={{ 
                    transform: totalDates > 14 ? 'rotate(-45deg)' : 'none',
                    transformOrigin: `${x}px ${chartHeight + padding - bottomPadding + yOffset}px`
                  }}
                >
                  {formatDate(date.toISOString().split('T')[0])}
                </text>
              );
            })}

            {/* Data lines */}
            {series.map((s, seriesIndex) => {
              // Create data points for all dates, with null for missing data
              const dataPoints = allDates.map(date => {
                const dateStr = date.toISOString().split('T')[0];
                const dataPoint = data.find(item => item.date === dateStr);
                if (!dataPoint) return null;
                
                // Return the appropriate average based on the series key
                let value = null;
                switch (s.key) {
                  case 'sbp': value = dataPoint.sbp_average || null; break;
                  case 'dbp': value = dataPoint.dbp_average || null; break;
                  case 'map': value = dataPoint.map_average || null; break;
                  case 'hr': value = dataPoint.hr_average || null; break;
                  case 'hrv': value = dataPoint.hrv_average || null; break;
                  default: value = null;
                }
                
                console.log(`🔍 Data point for ${s.key} on ${dateStr}:`, {
                  dataPoint: dataPoint,
                  value: value,
                  readings: dataPoint[s.key + '_readings'] || []
                });
                
                return value;
              });

              // Filter out null values for the polyline
              const validPoints = dataPoints
                .map((value, index) => ({ value, index }))
                .filter(point => point.value !== null);

              const points = validPoints.map(point => {
                const x = getX(point.index);
                const y = getY(point.value);
                console.log(`🔍 Creating polyline point for ${s.key}: index=${point.index}, value=${point.value}, x=${x}, y=${y}`);
                return `${x},${y}`;
              }).join(' ');

              return (
                <g key={seriesIndex}>
                  <polyline
                    points={points}
                    fill="none"
                    stroke={s.color}
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  
                  {/* Render individual readings as small points */}
                  {dataPoints.map((value, index) => {
                    if (value === null) return null;
                    
                    const x = getX(index);
                    const dateStr = allDates[index].toISOString().split('T')[0];
                    const dataPoint = data.find(item => item.date === dateStr);
                    
                    // Get the appropriate readings array based on the series key
                    let readings = [];
                    switch (s.key) {
                      case 'sbp': readings = dataPoint.sbp_readings || []; break;
                      case 'dbp': readings = dataPoint.dbp_readings || []; break;
                      case 'map': readings = dataPoint.map_readings || []; break;
                      case 'hr': readings = dataPoint.hr_readings || []; break;
                      case 'hrv': readings = dataPoint.hrv_readings || []; break;
                      default: readings = [];
                    }
                    
                    // Render individual readings as small gray dots
                    console.log(`🔍 Rendering individual readings for ${s.key}:`, {
                      date: dataPoint.date,
                      readingsCount: readings.length,
                      readings: readings,
                      seriesKey: s.key
                    });
                    
                    return readings.map((reading, readingIndex) => {
                      const y = getY(reading);
                      console.log(`🔍 Rendering reading ${readingIndex}:`, {
                        reading: reading,
                        y: y,
                        seriesKey: s.key
                      });
                      
                      // Add slight offset to prevent overlapping dots
                      const offsetX = (readingIndex - (readings.length - 1) / 2) * 3;
                      
                      return (
                        <circle
                          key={`${index}-reading-${readingIndex}-${seriesIndex}`}
                          cx={x + offsetX}
                          cy={y}
                          r="3"
                          fill="#9CA3AF"
                          stroke="white"
                          strokeWidth="1"
                          style={{ cursor: 'pointer' }}
                          onMouseEnter={(e) => handleMouseEnter(e, { 
                            date: dataPoint.date, 
                            [s.key]: reading,
                            sbp: s.key === 'sbp' ? reading : dataPoint.metadata.sbp,
                            dbp: s.key === 'dbp' ? reading : dataPoint.metadata.dbp,
                            map: s.key === 'map' ? reading : dataPoint.metadata.map,
                            hr: s.key === 'hr' ? reading : dataPoint.metadata.hr,
                            hrv: s.key === 'hrv' ? reading : dataPoint.metadata.hrv,
                            rhythm: dataPoint.metadata.rhythm,
                            quality_flag: dataPoint.metadata.quality_flag,
                            metadata: dataPoint.metadata 
                          }, s.key, seriesIndex)}
                          onMouseLeave={handleMouseLeave}
                        />
                      );
                    });
                  })}
                  
                  {/* Render average as larger circle connected to trendline */}
                  {dataPoints.map((value, index) => {
                    if (value === null) return null;
                    
                    const x = getX(index);
                    const y = getY(value);
                    const dateStr = allDates[index].toISOString().split('T')[0];
                    const dataPoint = data.find(item => item.date === dateStr);
                    
                    // Get the appropriate readings array based on the series key
                    let readings = [];
                    switch (s.key) {
                      case 'sbp': readings = dataPoint.sbp_readings || []; break;
                      case 'dbp': readings = dataPoint.dbp_readings || []; break;
                      case 'map': readings = dataPoint.map_readings || []; break;
                      case 'hr': readings = dataPoint.hr_readings || []; break;
                      case 'hrv': readings = dataPoint.hrv_readings || []; break;
                      default: readings = [];
                    }
                    
                    return (
                      <circle
                        key={`${index}-average-${seriesIndex}`}
                        cx={x}
                        cy={y}
                        r="6"
                        fill={s.color}
                        stroke="white"
                        strokeWidth="2"
                        style={{ cursor: 'pointer' }}
                        onMouseEnter={(e) => handleMouseEnter(e, { 
                          date: dataPoint.date, 
                          [s.key]: value,
                          sbp: s.key === 'sbp' ? value : dataPoint.sbp_average,
                          dbp: s.key === 'dbp' ? value : dataPoint.dbp_average,
                          map: s.key === 'map' ? value : dataPoint.map_average,
                          hr: s.key === 'hr' ? value : dataPoint.hr_average,
                          hrv: s.key === 'hrv' ? value : dataPoint.hrv_average,
                          readings: readings,
                          metadata: dataPoint.metadata 
                        }, s.key, seriesIndex)}
                        onMouseLeave={handleMouseLeave}
                      />
                    );
                  })}
                </g>
              );
            })}
          </svg>

          {/* Hover Tooltip */}
          {hoveredPoint && hoveredPoint.chartId === chartId && (
            <div
              className="absolute bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm z-50 min-w-[200px] pointer-events-none"
              style={{
                left: Math.min(Math.max(hoveredPoint.x + 15, 10), chartWidth - 220),
                top: Math.max(Math.min(hoveredPoint.y - 80, chartHeight - 100), 10),
                maxWidth: '250px'
              }}
            >
              <div className="font-medium text-[#0B1C2C] mb-2 text-center border-b border-gray-100 pb-1">
                {formatDate(hoveredPoint.data.date)}
              </div>
              <div className="text-[#374151] text-center">
                {hoveredPoint.tooltipFormat(hoveredPoint.data)}
              </div>
            </div>
          )}
        </div>

        {/* Legend */}
        <div className="flex justify-center space-x-6 mt-4">
          {series.map((s, index) => (
            <div key={index} className="flex items-center space-x-2">
              <div 
                className="w-4 h-4 rounded-full border-2"
                style={{ backgroundColor: s.color, borderColor: s.color }}
              ></div>
              <span className="text-sm font-medium text-[#374151]">{s.label}</span>
            </div>
          ))}
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full bg-gray-400 border border-white"></div>
            <span className="text-xs text-[#6B7280]">Individual readings</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full border-2 border-white bg-blue-600"></div>
            <span className="text-xs text-[#6B7280]">Daily averages</span>
          </div>
        </div>
      </div>
    );
  };

  const bloodPressureTooltip = (data) => {
    // For individual readings, the data contains the specific reading value
    const formatValue = (value) => {
      if (value === null || value === undefined) return 'N/A';
      return typeof value === 'number' ? value.toFixed(1) : value;
    };
    
    // Get values from data or metadata
    const sbp = formatValue(data.sbp || data.metadata?.sbp || data.blood_pressure_systolic);
    const dbp = formatValue(data.dbp || data.metadata?.dbp || data.blood_pressure_diastolic);
    const map = formatValue(data.map || data.metadata?.map);
    const readings = data.readings ? `(${data.readings.length} readings)` : '';
    
    return `SBP: ${sbp} | DBP: ${dbp} | MAP: ${map} ${readings}`;
  };

  const heartRateTooltip = (data) => {
    // For individual readings, the data contains the specific reading value
    const formatValue = (value) => {
      if (value === null || value === undefined) return 'N/A';
      return typeof value === 'number' ? value.toFixed(1) : value;
    };
    
    // Get values from data or metadata
    const hr = formatValue(data.hr || data.metadata?.hr || data.heart_rate);
    const hrv = formatValue(data.hrv || data.metadata?.hrv || data.heart_rate_variability);
    const rhythm = data.rhythm || data.metadata?.rhythm || (data.rr === 1 ? 'Regular' : 'Irregular') || 'N/A';
    const readings = data.readings ? `(${data.readings.length} readings)` : '';
    
    return `HR: ${hr} BPM | HRV: ${hrv} ms | Rhythm: ${rhythm} ${readings}`;
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#207CE5] mx-auto mb-4"></div>
            <p className="text-[#3D3D3D]">Loading health trends...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto relative">
          {/* Close button at top right */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XCircle className="h-6 w-6" />
          </button>
          
          <div className="text-center">
            <div className="text-red-500 text-6xl mb-4">⚠️</div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Data</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={() => fetchPatientVitals(0)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show no data state
  if (heartRateData.length === 0 && bloodPressureData.length === 0) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="text-center">
            <div className="text-gray-400 text-6xl mb-4">📊</div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Health Data Available</h3>
            <p className="text-gray-600 mb-4">
              No vital signs recordings found for {patient?.alias || 'this patient'}. 
              Health trends will appear here once recordings are completed.
            </p>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-7xl w-full max-h-[95vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-[#E5E7EB]">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-[#0B1C2C]">
                Health Trends - {patient?.alias || 'Patient'}
              </h2>
              <p className="text-sm text-[#3D3D3D] mt-1">
                Blood pressure and heart rate monitoring over time
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-[#3D3D3D] hover:text-[#207CE5]"
            >
              <XCircle className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Date Range Selector */}
        <div className="p-6 border-b border-[#E5E7EB]">
          <div className="flex items-center space-x-4 flex-wrap">
            <span className="text-sm font-medium text-[#3D3D3D]">Select Range</span>
            <button
              onClick={() => handleDateRangeChange('today')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                selectedRange === 'today'
                  ? 'bg-[#207CE5] text-white'
                  : 'bg-[#F9FBFC] text-[#3D3D3D] hover:bg-[#DCEAF5]'
              }`}
            >
              Today
            </button>
            <button
              onClick={() => handleDateRangeChange('7days')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                selectedRange === '7days'
                  ? 'bg-[#207CE5] text-white'
                  : 'bg-[#F9FBFC] text-[#3D3D3D] hover:bg-[#DCEAF5]'
              }`}
            >
              Last 7 days
            </button>
            <button
              onClick={() => handleDateRangeChange('30days')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                selectedRange === '30days'
                  ? 'bg-[#207CE5] text-white'
                  : 'bg-[#F9FBFC] text-[#3D3D3D] hover:bg-[#DCEAF5]'
              }`}
            >
              Last 30 days
            </button>
            <button
              onClick={() => setShowCalendar(!showCalendar)}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center space-x-2 ${
                selectedRange === 'custom'
                  ? 'bg-[#207CE5] text-white'
                  : 'bg-[#F9FBFC] text-[#3D3D3D] hover:bg-[#DCEAF5]'
              }`}
            >
              <Calendar className="h-4 w-4" />
              <span>Custom Range</span>
            </button>
          </div>

          {/* Custom Range Calendar */}
          {showCalendar && (
            <div className="mt-4 p-3 bg-[#F9FBFC] rounded-lg border border-[#E5E7EB] max-w-md mx-auto">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-xs font-medium text-[#0B1C2C]">Select Custom Date Range</h4>
                <button
                  onClick={() => setShowCalendar(false)}
                  className="text-[#3D3D3D] hover:text-[#207CE5]"
                >
                  <XCircle className="h-3 w-3" />
                </button>
              </div>
              
              <div className="grid grid-cols-2 gap-2 mb-3">
                <div>
                  <label className="block text-xs font-medium text-[#3D3D3D] mb-1">Start Date</label>
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    className="w-full px-2 py-1 border border-[#E5E7EB] rounded text-xs focus:outline-none focus:ring-1 focus:ring-[#207CE5] focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#3D3D3D] mb-1">End Date</label>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    className="w-full px-2 py-1 border border-[#E5E7EB] rounded text-xs focus:outline-none focus:ring-1 focus:ring-[#207CE5] focus:border-transparent"
                  />
                </div>
              </div>

              {/* Calendar Component */}
              <div className="bg-white rounded border border-[#E5E7EB] p-3">
                <div className="flex items-center justify-between mb-2">
                  <button
                    onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
                    className="p-1 hover:bg-[#F3F4F6] rounded"
                  >
                    <ChevronLeft className="h-3 w-3 text-[#3D3D3D]" />
                  </button>
                  <h5 className="text-xs font-medium text-[#0B1C2C]">
                    {currentMonth.toLocaleDateString('en-US', { month: 'short', year: '2-digit' })}
                  </h5>
                  <button
                    onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
                    className="p-1 hover:bg-[#F3F4F6] rounded"
                  >
                    <ChevronRight className="h-3 w-3 text-[#3D3D3D]" />
                  </button>
                </div>

                {/* Calendar Grid */}
                <div className="grid grid-cols-7 gap-0.5">
                  {/* Day headers */}
                  {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map(day => (
                    <div key={day} className="text-xs font-medium text-[#3D3D3D] text-center py-1">
                      {day}
                    </div>
                  ))}
                  
                  {/* Calendar days */}
                  {(() => {
                    const daysInMonth = getDaysInMonth(currentMonth);
                    const firstDayOfMonth = getFirstDayOfMonth(currentMonth);
                    const days = [];
                    
                    // Add empty cells for days before the first day of the month
                    for (let i = 0; i < firstDayOfMonth; i++) {
                      days.push(<div key={`empty-${i}`} className="h-6"></div>);
                    }
                    
                    // Add days of the month
                    for (let day = 1; day <= daysInMonth; day++) {
                      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
                      const dateString = formatDateForInput(date);
                      const isSelected = isDateInRange(dateString, customStartDate, customEndDate);
                      const isToday = dateString === formatDateForInput(new Date());
                      
                      days.push(
                        <button
                          key={day}
                          onClick={() => {
                            if (!customStartDate || (customStartDate && customEndDate)) {
                              setCustomStartDate(dateString);
                              setCustomEndDate('');
                            } else {
                              if (dateString >= customStartDate) {
                                setCustomEndDate(dateString);
                              } else {
                                setCustomStartDate(dateString);
                                setCustomEndDate('');
                              }
                            }
                          }}
                          className={`h-6 w-full text-xs rounded transition-colors ${
                            isSelected
                              ? 'bg-[#207CE5] text-white'
                              : isToday
                              ? 'bg-[#DCEAF5] text-[#207CE5] font-medium'
                              : 'hover:bg-[#F3F4F6] text-[#3D3D3D]'
                          }`}
                        >
                          {day}
                        </button>
                      );
                    }
                    
                    return days;
                  })()}
                </div>
              </div>

              {/* Apply Button */}
              <div className="flex justify-end mt-3">
                <button
                  onClick={handleCustomRangeApply}
                  disabled={!customStartDate || !customEndDate}
                  className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                    customStartDate && customEndDate
                      ? 'bg-[#207CE5] text-white hover:bg-[#1E40AF]'
                      : 'bg-[#E5E7EB] text-[#9CA3AF] cursor-not-allowed'
                  }`}
                >
                  Apply Range
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Charts */}
        <div className="p-6">
          {/* Blood Pressure Trend */}
          {renderChart(
            filteredBloodPressureData,
            'Blood Pressure Trend',
            { min: 0, max: 200 },
            [
              { key: 'sbp', label: 'SBP', color: '#1E40AF' },
              { key: 'dbp', label: 'DBP', color: '#3B82F6' },
              { key: 'map', label: 'MAP', color: '#3D3D3D' }
            ],
            bloodPressureTooltip
          )}

          {/* Heart Rate Trend */}
          {renderChart(
            filteredHeartRateData,
            'Heart Rate Trend',
            { min: 0, max: 150 },
            [
              { key: 'hr', label: 'HR', color: '#1E40AF' },
              { key: 'hrv', label: 'HRV', color: '#3B82F6' }
            ],
            heartRateTooltip
          )}

          {/* Summary Section - Average Values */}
          <div className="mt-8">
            <h3 className="text-lg font-semibold text-[#0B1C2C] text-center mb-6">Average Values</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {/* Blood Pressure Average */}
              <div className="bg-white p-6 rounded-lg border border-gray-200 text-center shadow-sm">
                <h4 className="text-sm font-medium text-gray-600 mb-2">Blood Pressure</h4>
                <div className="text-lg font-bold text-gray-900">
                  {(() => {
                    const allBPData = [...bloodPressureData];
                    if (allBPData.length === 0) return 'N/A';
                    
                    const allSBP = allBPData.flatMap(item => item.sbp_readings || []);
                    const allDBP = allBPData.flatMap(item => item.dbp_readings || []);
                    
                    if (allSBP.length === 0 || allDBP.length === 0) return 'N/A';
                    
                    const avgSBP = allSBP.reduce((sum, val) => sum + val, 0) / allSBP.length;
                    const avgDBP = allDBP.reduce((sum, val) => sum + val, 0) / allDBP.length;
                    
                    return `${Math.round(avgSBP)}/${Math.round(avgDBP)}`;
                  })()}
                </div>
                <div className="text-xs text-gray-500 mt-1">mmHg</div>
              </div>

              {/* Heart Rate Average */}
              <div className="bg-white p-6 rounded-lg border border-gray-200 text-center shadow-sm">
                <h4 className="text-sm font-medium text-gray-600 mb-2">Heart Rate</h4>
                <div className="text-lg font-bold text-gray-900">
                  {(() => {
                    const allHRData = [...heartRateData];
                    if (allHRData.length === 0) return 'N/A';
                    
                    const allHR = allHRData.flatMap(item => item.hr_readings || []);
                    if (allHR.length === 0) return 'N/A';
                    
                    const avgHR = allHR.reduce((sum, val) => sum + val, 0) / allHR.length;
                    return Math.round(avgHR);
                  })()}
                </div>
                <div className="text-xs text-gray-500 mt-1">BPM</div>
              </div>

              {/* Heart Rate Variability Average */}
              <div className="bg-white p-6 rounded-lg border border-gray-200 text-center shadow-sm">
                <h4 className="text-sm font-medium text-gray-600 mb-2">HRV</h4>
                <div className="text-lg font-bold text-gray-900">
                  {(() => {
                    const allHRData = [...heartRateData];
                    if (allHRData.length === 0) return 'N/A';
                    
                    const allHRV = allHRData.flatMap(item => item.hrv_readings || []);
                    if (allHRV.length === 0) return 'N/A';
                    
                    const avgHRV = allHRV.reduce((sum, val) => sum + val, 0) / allHRV.length;
                    return Math.round(avgHRV);
                  })()}
                </div>
                <div className="text-xs text-gray-500 mt-1">ms</div>
              </div>

              {/* Rhythm Regularity Average */}
              <div className="bg-white p-6 rounded-lg border border-gray-200 text-center shadow-sm">
                <h4 className="text-sm font-medium text-gray-600 mb-2">Rhythm Regularity</h4>
                <div className="text-lg font-bold text-gray-900">
                  {(() => {
                    const allHRData = [...heartRateData];
                    if (allHRData.length === 0) return 'N/A';
                    
                    // Collect all rhythm readings from all dates
                    const allRhythmReadings = allHRData.flatMap(item => item.rhythm_readings || []);
                    
                    if (allRhythmReadings.length === 0) return 'N/A';
                    
                    // Count regular vs irregular rhythms (1 = regular, 0 = irregular)
                    const regularCount = allRhythmReadings.filter(rr => rr === 1).length;
                    const totalCount = allRhythmReadings.length;
                    
                    const regularityPercentage = (regularCount / totalCount) * 100;
                    return regularityPercentage >= 70 ? 'Regular' : 'Irregular';
                  })()}
                </div>
                <div className="text-xs text-gray-500 mt-1">Pattern</div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default PatientHealthTrends;
